import React, {useState, useEffect, useRef} from 'react';
import {useAuth0} from "@auth0/auth0-react";
import {fetchOneOrder, reportInvoiceError} from "../apiRequests/api";
import {pdf} from '@react-pdf/renderer';
import Invoice from "./Invoice";
import InvoiceErrorModal from "./InvoiceErrorModal";

function InvoiceDownloadLink({orderNo, invoiceNumber, userName}) {
    const [isGenerating, setIsGenerating] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);
    const [showError, setShowError] = useState(false);
    const {getIdTokenClaims} = useAuth0();
    const abortControllerRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    async function reportError(error, order) {
        try {
            const token = await getIdTokenClaims();
            reportInvoiceError({
                orderNo,
                invoiceNumber,
                CustomerAccount: userName,
                message: String(error && error.message),
                stack: error && error.stack,
                order: order ? JSON.stringify(order, null, 2) : undefined,
            }, token.__raw);
        } catch (e) {
            // Reporting is best effort.
        }
    }

    async function handleGenerateAndOpenInvoice() {
        if (isGenerating) return;

        // Open the tab during the click, before any await, so popup blockers allow it.
        const invoiceWindow = window.open('', '_blank');

        setIsGenerating(true);
        abortControllerRef.current = new AbortController();

        let singleOrder;
        try {
            // Fetch order data
            const token = await getIdTokenClaims();
            const response = await fetchOneOrder(orderNo, userName, token.__raw);
            singleOrder = response && response.mainResult;

            if (!singleOrder) {
                throw new Error(`Order not found for orderNo: ${orderNo}`);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error fetching order for invoice:', error);
                invoiceWindow?.close();
                reportError(error);
                setIsInvalid(true);
                setShowError(true);
            }
            setIsGenerating(false);
            abortControllerRef.current = null;
            return;
        }

        try {
            let orderArray = [];
            if (singleOrder && singleOrder.OrderLines && singleOrder.OrderLines.OrderLine) {
                if (Array.isArray(singleOrder.OrderLines.OrderLine)) {
                    orderArray = singleOrder.OrderLines.OrderLine;
                } else {
                    orderArray.push(singleOrder.OrderLines.OrderLine);
                }
            }

            const totalNett = orderArray.reduce((sum, order) => (sum + order.LineNett), 0);
            const totalVAT = orderArray.reduce((sum, order) => (sum + order.LineVAT), 0);
            const grandTotal = orderArray.reduce((sum, order) => (sum + order.LineVAT + order.LineNett), 0);

            // Generate PDF blob
            const invoiceDoc = <Invoice
                order={singleOrder}
                orderArray={orderArray}
                totalNett={totalNett}
                totalVAT={totalVAT}
                grandTotal={grandTotal}
                currency={window.currency || '£'}
            />;

            const blob = await pdf(invoiceDoc).toBlob();
            const url = URL.createObjectURL(blob);

            if (invoiceWindow) {
                invoiceWindow.location.href = url;
            } else {
                window.location.href = url;
            }

            // Keep the URL alive long enough for the new tab to load it.
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 60000);

        } catch (error) {
            invoiceWindow?.close();
            if (error.name !== 'AbortError') {
                console.error('Error generating invoice:', error);
                reportError(error, singleOrder);
                setShowError(true);
            }
        } finally {
            setIsGenerating(false);
            abortControllerRef.current = null;
        }
    }

    const errorModal = (
        <InvoiceErrorModal
            open={showError}
            invoiceNumber={invoiceNumber}
            onClose={() => setShowError(false)}
        />
    );

    if (isInvalid) {
        return <>
            <p>{invoiceNumber}</p>
            {errorModal}
        </>;
    }

    return (
        <>
        <a
            href="#"
            onClick={(e) => {
                e.preventDefault();
                handleGenerateAndOpenInvoice();
            }}
            style={{cursor: 'pointer'}}
        >
            <p>{isGenerating ? 'Generating...' : invoiceNumber}</p>
        </a>
        {errorModal}
        </>
    );
}

export default InvoiceDownloadLink;